// src/admin_module/components/customer_reviews/AddReviewModal.jsx

import React, { useState, useEffect } from "react";
import { FaStar } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";

const MAX_CHARS = 300;

const AddReviewModal = ({
  open,
  onClose,
  onSubmit,
  initialRating = 0,
  initialComment = "",
  isEdit = false,
}) => {
  const [rating, setRating] = useState(initialRating);
  const [hovered, setHovered] = useState(0);
  const [comment, setComment] = useState(initialComment);

  // Reset state whenever modal opens or props change
  useEffect(() => {
    if (open) {
      setRating(initialRating);
      setComment(initialComment);
    }
  }, [open, initialRating, initialComment]);

  if (!open) return null;

  const handleSubmit = () => {
    if (rating === 0) {
      alert("Please select a rating");
      return;
    }
    if (comment.trim().length < 10) {
      alert("Review must be at least 10 characters");
      return;
    }

    // Always send { rating, review } to backend
    onSubmit({ rating, review: comment });
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={overlayStyle}
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ duration: 0.25 }}
          style={modalStyle}
          onClick={(e) => e.stopPropagation()} // prevent closing modal on inner click
        >
          {/* HEADER */}
          <h3 style={{ marginBottom: 10 }}>
            {isEdit ? "Edit Review" : "Add a Review"}
          </h3>
          <p style={{ opacity: 0.7, fontSize: 14, marginBottom: 18 }}>
            {isEdit
              ? "Update the review for this user."
              : "Your feedback helps others build confidence."}
          </p>

          {/* STAR RATING */}
          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle}>Rating</label>
            <div style={{ display: "flex", gap: 8 }}>
              {[1, 2, 3, 4, 5].map((i) => (
                <FaStar
                  key={i}
                  size={26}
                  cursor="pointer"
                  color={i <= (hovered || rating) ? "#FFD700" : "#DDD"}
                  onMouseEnter={() => setHovered(i)}
                  onMouseLeave={() => setHovered(0)}
                  onClick={() => setRating(i)}
                />
              ))}
            </div>
          </div>

          {/* TEXTAREA */}
          <div style={{ marginBottom: 22 }}>
            <label style={labelStyle}>Review</label>
            <textarea
              value={comment}
              onChange={(e) =>
                e.target.value.length <= MAX_CHARS && setComment(e.target.value)
              }
              placeholder="Write your review..."
              style={textareaStyle}
            />
            <div style={{ fontSize: 12, opacity: 0.6, textAlign: "right" }}>
              {comment.length} / {MAX_CHARS}
            </div>
          </div>

          {/* ACTIONS */}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
            <button onClick={onClose} style={cancelBtn}>
              Cancel
            </button>
            <button onClick={handleSubmit} style={submitBtn}>
              {isEdit ? "Update Review" : "Submit Review"}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

/* ================= STYLES ================= */
const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.45)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 999,
};

const modalStyle = {
  background: "#fff",
  borderRadius: 16,
  padding: 26,
  width: "90%",
  maxWidth: 420,
  boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
};

const labelStyle = {
  display: "block",
  fontSize: 14,
  fontWeight: 600,
  marginBottom: 6,
};

const textareaStyle = {
  width: "100%",
  minHeight: 110,
  resize: "none",
  padding: 12,
  borderRadius: 10,
  border: "1px solid #ddd",
  fontSize: 14,
  outline: "none",
};

const cancelBtn = {
  background: "transparent",
  border: "none",
  fontSize: 14,
  cursor: "pointer",
  opacity: 0.7,
};

const submitBtn = {
  padding: "8px 20px",
  borderRadius: 20,
  border: "none",
  cursor: "pointer",
  fontWeight: 600,
  background: "linear-gradient(90deg, #6a11cb, #2575fc)",
  color: "#fff",
};

export default AddReviewModal;
